import { useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { apiInstance } from "@/shared/api";
import { filterObjectKeys } from "@/shared/lib/utils";
import type { PaginatedResponse, PaginationRequest } from "@/shared/types";
import type { User } from "@/entities/user";
import type { Manager } from "../model";
import type {
  AssignTeamleadToManagerRequest,
  AssignTeamleadToManagerResponse,
} from "./api.dto";

export const useAssignTeamleadToManager = () => {
  return useMutation<
    AssignTeamleadToManagerResponse,
    AxiosError,
    AssignTeamleadToManagerRequest
  >({
    mutationKey: ["assignTeamleadToManager"],
    mutationFn: (data) => {
      return apiInstance.patch(
        `/managers/${encodeURI(data.managerId)}/teamlead/${encodeURI(data.teamleadId)}`,
      );
    },
  });
};

export const useGetAllManagers = (params: PaginationRequest) => {
  return useQuery<PaginatedResponse<Manager>>({
    queryKey: ["managers", params],
    queryFn: async () => {
      const { data } = await apiInstance.get<PaginatedResponse<Manager>>(
        "/managers",
        filterObjectKeys(params),
      );

      return data;
    },
  });
};

export const useGetManagersByTeamlead = (
  params: PaginationRequest & { userId: string },
) => {
  const filteredParams = filterObjectKeys(params, ["userId"]);

  return useQuery<PaginatedResponse<Manager>>({
    queryKey: ["managersByTeamlead", filteredParams],
    queryFn: async () => {
      const { data } = await apiInstance.get<PaginatedResponse<Manager>>(
        `/managers/teamlead/${params.userId}`,
        filteredParams,
      );

      return data;
    },
  });
};

export const useGetManagersByMasterteamlead = (
  params: PaginationRequest & { userId: string; enabled?: boolean },
) => {
  return useQuery<
    PaginatedResponse<Manager & { userId: User; teamlead: string }>
  >({
    queryKey: [
      "managersByMasterteamlead",
      filterObjectKeys(params, ["enabled"]),
    ],
    queryFn: async () => {
      const { data } = await apiInstance.get<
        PaginatedResponse<Manager & { userId: User; teamlead: string }>
      >(
        `/managers/master-teamlead/${params.userId}`,
        filterObjectKeys(params, ["userId", "enabled"]),
      );

      return data;
    },
    enabled: params.enabled,
  });
};
